import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, FormField, Input, useToast } from "@/design-system/components/ui";
import { calendarDateInputToIso } from "@/design-system/lib/format";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { buyerActionsApi } from "@/api/buyerActions";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";

export interface ScheduleFollowUpModalProps {
  buyerLeadId: string;
  buyerName: string;
  onClose: () => void;
  onScheduled?: (taskId: string) => void;
}

/**
 * Schedule the next touch on a buyer — a WorkTask related to the buyer, due
 * on a day the specialist picks.
 *
 * <p>The buyer no longer carries a follow-up DATE of its own; the follow-up is
 * a task, so it has a title, an assignee and an open/done state, and the list's
 * "next action" column and the needs-action badges read it back. The date
 * crosses the wire through `calendarDateInputToIso` — a `<input type="date">`
 * value is a wall-calendar day.</p>
 */
export function ScheduleFollowUpModal({
  buyerLeadId,
  buyerName,
  onClose,
  onScheduled,
}: ScheduleFollowUpModalProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState(`Follow up with ${buyerName}`);
  const [date, setDate] = useState("");

  const dueIso = calendarDateInputToIso(date);
  const canSubmit = title.trim().length > 0 && dueIso != null;

  const mutation = useMutation({
    mutationFn: () =>
      buyerActionsApi.scheduleFollowUp(buyerLeadId, { title: title.trim(), dueDate: dueIso! }),
    onSuccess: (taskId) => {
      // The row's next action and the due-today / overdue stat cards read the
      // task this created.
      qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success("Follow-up scheduled.");
      onScheduled?.(taskId);
      onClose();
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e)),
  });

  return (
    <Modal open onClose={onClose} title={`Schedule follow-up — ${buyerName}`} size="sm">
      <form
        className="grid gap-space-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit && !mutation.isPending) mutation.mutate();
        }}
      >
        <FormField label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="follow-up-title"
          />
        </FormField>
        <FormField label="Due" required>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="follow-up-date"
          />
        </FormField>
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" type="button" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            disabled={!canSubmit || mutation.isPending}
            data-testid="follow-up-submit"
          >
            {mutation.isPending ? "Scheduling…" : "Schedule"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
